import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { BOX_MAX_WIDTH } from "utils/constants";
import { format, FORMATS, parseJSON } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";

import Box from "components/Box";
import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import { usePageTitle } from "components/usePageTitle";

import ActionButtons from "entities/TableStorage/components/ActionButtons";
import Table from "entities/TableStorage/components/Table";
import TableSkeleton from "entities/TableStorage/components/TableSkeleton";
import TableUpdateDialogButton from "entities/TableStorage/components/TableUpdateDialogButton";
import {
  tableDelete,
  tableUpdate,
  TableUpdateData,
  useTableRetrieve,
} from "entities/TableStorage/sdk";

const Details = () => {
  const navigate = useNavigate();

  const params = useParams<{
    tableId: string;
  }>();
  const tableId = Number(params.tableId);

  const { data: table, mutate: mutateTable } = useTableRetrieve({ tableId });

  usePageTitle(
    !_.isUndefined(table)
      ? `Builders Patch: ${table.name} - Table storage`
      : "Builders Patch: Table storage"
  );

  const tableLastUpdatedLabel = useMemo(() => {
    if (_.isNil(table)) {
      return "";
    }

    if (!_.isNil(table.updated_by)) {
      return `Last edited ${format(
        parseJSON(table.updated_at),
        FORMATS.FRONTEND.DATETIME
      )} by ${table.updated_by.first_name} ${table.updated_by.last_name}`;
    }

    return `Last edited ${format(
      parseJSON(table.created_at),
      FORMATS.FRONTEND.DATETIME
    )} by ${table.created_by.first_name} ${table.created_by.last_name}`;
  }, [table]);

  const handleTableUpdate = useCallback(
    ({ values }: { values: TableUpdateData }) =>
      tableUpdate({ tableId, data: values })
        .then(() => {
          toast.successMessage("Table updated successfully!");
          mutateTable();
        })
        .catch(handleInvalidRequest),
    [tableId, mutateTable]
  );

  const handleTableDelete = useCallback(
    ({ tableId }) =>
      tableDelete({ tableId })
        .then(() => {
          toast.successMessage("Table deleted successfully!");
          navigate(URLS.TABLE_STORAGE_LIST);
        })
        .catch(handleInvalidRequest),
    [navigate]
  );

  return (
    <Paper
      sx={{
        overflowX: "auto",
        padding: theme.spacing(3, 4),
      }}
    >
      <Stack>
        <Stack
          spacing={4}
          sx={{ maxWidth: BOX_MAX_WIDTH, alignSelf: "center", width: "100%" }}
        >
          <PageHeader
            title={
              _.isUndefined(table) ? (
                <Skeleton height={55} width={250} />
              ) : (
                <Stack
                  direction="row"
                  spacing={theme.spacing(1)}
                  alignItems="center"
                >
                  <Text variant="h2" data-testid="page-header-name">
                    {table.name}
                  </Text>
                </Stack>
              )
            }
            subTitle={tableLastUpdatedLabel}
            backTitle="Company-level data tables"
            backLink={URLS.TABLE_STORAGE_LIST}
            actions={
              _.isUndefined(table) ? (
                <Skeleton height={60} width={50} />
              ) : (
                <ActionButtons
                  tableId={table.id}
                  initialValue={table.name}
                  onUpdate={handleTableUpdate}
                  onDelete={() =>
                    handleTableDelete({
                      tableId: table.id,
                    })
                  }
                />
              )
            }
          />
          <Stack spacing={2}>
            <Box>
              {_.isUndefined(table) ? (
                <Skeleton height={38} width={150} />
              ) : (
                <TableUpdateDialogButton onUpdate={handleTableUpdate} />
              )}
            </Box>
            {_.isUndefined(table) ? (
              <TableSkeleton />
            ) : (
              <Table tableData={table.rows} />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default Details;
